import constate from 'constate';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import { UseFieldArrayAppend, UseFieldArrayRemove, UseFieldArrayUpdate } from 'react-hook-form';

import { DB_DATE_FORMAT } from 'core/constants';
import { useFormContext } from 'core/states';
import { IUpsertLogsFormData } from 'jobs/components/JobDetails';

interface IUseTimeLogIntervalProps {
  append: UseFieldArrayAppend<IUpsertLogsFormData, 'timeLogs'>;
  update: UseFieldArrayUpdate<IUpsertLogsFormData, 'timeLogs'>;
  remove: UseFieldArrayRemove;
}

const useTimeLogInterval = ({ append, update, remove }: IUseTimeLogIntervalProps) => {
  const [intervalStart, setIntervalStart] = useState<Dayjs | null>(null);
  const [intervalEnd, setIntervalEnd] = useState<Dayjs | null>(null);
  const [intervalHours, setIntervalHours] = useState<number | null>(null);
  const [hoveredDate, setHoveredDate] = useState<Dayjs | null>(null);

  const { watch } = useFormContext();

  const getDatesInInterval = useCallback((start: Dayjs, end: Dayjs): Dayjs[] => {
    const dates: Dayjs[] = [];
    let current = start;

    while (current.isSame(end, 'day') || current.isBefore(end, 'day')) {
      dates.push(current);
      current = current.add(1, 'day');
    }

    return dates;
  }, []);

  const selectedDates = useMemo(() => {
    if (!intervalStart || !intervalEnd) {
      return [];
    }

    return getDatesInInterval(intervalStart, intervalEnd);
  }, [intervalStart, intervalEnd, getDatesInInterval]);

  const previewDates = useMemo(() => {
    if (!intervalStart || intervalEnd || !hoveredDate) {
      return [];
    }

    if (hoveredDate.isSame(intervalStart, 'day')) {
      return [];
    }

    const start = hoveredDate.isBefore(intervalStart, 'day') ? hoveredDate : intervalStart;
    const end = hoveredDate.isBefore(intervalStart, 'day') ? intervalStart : hoveredDate;

    return getDatesInInterval(start, end);
  }, [intervalStart, intervalEnd, hoveredDate, getDatesInInterval]);

  const onDayHover = useCallback((date: Dayjs | null) => {
    setHoveredDate(date);
  }, []);

  const onDayClick = useCallback(
    (date: Dayjs) => {
      // No start date yet - set it
      if (!intervalStart) {
        setIntervalStart(date);
        setIntervalEnd(null);

        return;
      }

      // Start date exists but no end date yet
      if (!intervalEnd) {
        // Clicked date is before start - swap them
        if (date.isBefore(intervalStart, 'day')) {
          setIntervalStart(date);
          setIntervalEnd(intervalStart);
          setHoveredDate(null);

          return;
        }

        // Clicked same date as start - clear selection
        if (date.isSame(intervalStart, 'day')) {
          setIntervalStart(null);

          return;
        }

        // Clicked date is after start - set as end
        setIntervalEnd(date);
        setHoveredDate(null);

        return;
      }

      // Both dates exist - start new selection
      setIntervalStart(date);
      setIntervalEnd(null);
      setHoveredDate(null);
    },
    [intervalStart, intervalEnd]
  );

  const clearInterval = useCallback(() => {
    setIntervalStart(null);
    setIntervalEnd(null);
    setHoveredDate(null);
  }, []);

  const onIntervalHoursChange = useCallback((hours: number | null) => {
    setIntervalHours(hours);
  }, []);

  const onSetIntervalTime = useCallback(() => {
    const currentTimeLogs = watch('timeLogs') || [];

    if (intervalHours === null) {
      return;
    }

    // Case 1: Positive hours - add or update time logs for selected dates
    if (intervalHours > 0) {
      selectedDates.forEach((date) => {
        const dateKey = date.format(DB_DATE_FORMAT);
        const existingTimeLogIndex = currentTimeLogs.findIndex(
          (timeLog: { activityDate: string }) =>
            dayjs(timeLog.activityDate).format(DB_DATE_FORMAT) === dateKey
        );

        // Update existing time log with new duration
        if (existingTimeLogIndex >= 0) {
          update(existingTimeLogIndex, {
            ...currentTimeLogs[existingTimeLogIndex],
            duration: intervalHours,
          });
        } else {
          // Create new time log entry
          append({
            activityDate: dateKey,
            duration: intervalHours,
          });
        }
      });
    } else {
      // Case 2: Zero or negative hours - remove time logs for selected dates
      const indicesToRemove: number[] = [];

      selectedDates.forEach((date) => {
        const dateKey = date.format(DB_DATE_FORMAT);
        currentTimeLogs.forEach((timeLog: { activityDate: string }, index: number) => {
          if (dayjs(timeLog.activityDate).format(DB_DATE_FORMAT) === dateKey) {
            indicesToRemove.push(index);
          }
        });
      });

      remove(indicesToRemove);
    }

    clearInterval();
    setIntervalHours(null);
  }, [intervalHours, selectedDates, watch, append, update, remove, clearInterval]);

  const onCancelInterval = useCallback(() => {
    clearInterval();
    setIntervalHours(null);
  }, [clearInterval]);

  return {
    clearInterval,
    intervalEnd,
    intervalHours,
    intervalStart,
    onCancelInterval,
    onDayClick,
    onDayHover,
    onIntervalHoursChange,
    onSetIntervalTime,
    previewDates,
    selectedDates,
  };
};

export const [TimeLogIntervalProvider, useTimeLogIntervalContext] = constate(useTimeLogInterval);
